import { useState } from "react";
import { FileUpload } from "../../atoms/FileUpload.jsx";
import { uploadIdDocument, uploadAuthLetter, submitForReview } from "../../services/persona-owner.js";

/**
 * ONB-P2: Verification document upload page.
 * Two uploads: Government ID + Authorisation Letter.
 *
 * Props:
 *   application — current application object
 *   onUpdate    — (updatedApp) => void
 *   onNext      — () => void — navigate to status page
 *   onBack      — () => void — go back to details
 */
export function Verification({ application, onUpdate, onNext, onBack }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const hasId = !!application?.id_document_url;
  const hasLetter = !!application?.auth_letter_url;
  const canSubmit = hasId && hasLetter;

  async function handleUploadId(file) {
    const { application: updated } = await uploadIdDocument(file);
    onUpdate?.(updated);
  }

  async function handleUploadLetter(file) {
    const { application: updated } = await uploadAuthLetter(file);
    onUpdate?.(updated);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const { application: updated } = await submitForReview();
      onUpdate?.(updated);
      onNext?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: 640, padding: "24px 28px" }}>
      <h1 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", fontFamily: "Georgia, serif", marginBottom: 8 }}>
        Verification Documents
      </h1>
      <p style={{ color: "#94a3b8", fontSize: 13, marginBottom: 28, lineHeight: 1.6 }}>
        Upload two documents to verify your identity and authorisation to manage this persona.
        Both documents are required before submitting for review.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {/* Government ID */}
        <div>
          <h3 style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            1. Government-Issued ID
          </h3>
          <p style={{ color: "#64748b", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
            National ID card, international passport, driver's licence, or voter's card.
            Must show your full name and photo clearly.
          </p>
          <FileUpload
            accept="image/jpeg,image/png,image/webp,application/pdf"
            maxSizeMB={5}
            onUpload={handleUploadId}
            label="Upload Government ID"
            hint="JPEG, PNG, WebP, or PDF. Max 5MB"
            uploaded={hasId}
            fileName={application?.id_document_url?.split("/").pop()}
          />
        </div>

        {/* Authorisation Letter */}
        <div>
          <h3 style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
            2. Authorisation Letter
          </h3>
          <p style={{ color: "#64748b", fontSize: 12, marginBottom: 12, lineHeight: 1.5 }}>
            A signed letter from the elected official authorising you to manage their digital clone.
            Must be on official letterhead with signature and stamp/seal.
          </p>
          <a
            href="/templates/authorisation-letter-template.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-block", marginBottom: 12, padding: "8px 16px",
              background: "#1e2d45", border: "1px solid #2d3f5a", borderRadius: 8,
              color: "#60a5fa", fontSize: 12, textDecoration: "none", fontFamily: "inherit",
            }}
          >
            Download Template
          </a>
          <FileUpload
            accept="image/jpeg,image/png,image/webp,application/pdf"
            maxSizeMB={5}
            onUpload={handleUploadLetter}
            label="Upload Authorisation Letter"
            hint="Signed letter on official letterhead. Max 5MB"
            uploaded={hasLetter}
            fileName={application?.auth_letter_url?.split("/").pop()}
          />
        </div>
      </div>

      {error && (
        <div style={{ color: "#ef4444", fontSize: 13, marginTop: 16, padding: "10px 14px", background: "#ef444415", borderRadius: 8 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginTop: 28 }}>
        <button
          onClick={onBack}
          style={{
            padding: "12px 24px", borderRadius: 8, border: "1px solid #2d3f5a",
            background: "#1e2d45", color: "#94a3b8", fontSize: 14,
            cursor: "pointer", fontFamily: "inherit",
          }}
        >
          \u2190 Back
        </button>

        <button
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
          style={{
            flex: 1, padding: "12px 24px", borderRadius: 8, border: "none",
            background: canSubmit ? "#16a34a" : "#1e2d45",
            color: canSubmit ? "#fff" : "#64748b",
            fontSize: 14, fontWeight: 600,
            cursor: canSubmit ? "pointer" : "not-allowed",
            fontFamily: "inherit",
          }}
        >
          {submitting ? "Submitting..." : "Submit for Review"}
        </button>
      </div>

      {!canSubmit && (
        <p style={{ color: "#64748b", fontSize: 11, marginTop: 12, textAlign: "center" }}>
          Both documents are required before submitting
        </p>
      )}
    </div>
  );
}
